import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDate, formatUnixByTimezone, titleCase } from 'utils/helperFunctions';
import { useGetAgencyAssociatedRecords, useGetAgencyCallLog } from 'apis/agency/callLogs/hook';
import { useCreateNote, useDeleteNote, useGetContact, useGetNotes, useUpdateNote } from 'apis/agency/contact/hook';
import InteractionLogForm from '@/components/Common/InteractionLogForm';
import type { FormValues, InteractionLogFormProps } from '@/components/Common/InteractionLogForm';
import { getVisibleJourneyItems } from '@/components/Common/InteractionLogForm/components/RightColumn/components/transcriptUtils';

function parseKeywords(signalData: Record<string, unknown>) {
  return Array.isArray(signalData?.keywords) ? signalData.keywords : [];
}

function parseWorkflows(signalData: Record<string, unknown>) {
  return Array.isArray(signalData?.workflows) ? signalData.workflows : [];
}

function parseAgencyTaskCompleted(signalData: Record<string, unknown>) {
  const tasks = signalData?.tasks_completed;
  if (Array.isArray(tasks)) return tasks;
  if (tasks && typeof tasks === 'object') {
    return Object.entries(tasks as Record<string, number | boolean>)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => {
        const label = titleCase(key.replace(/_/g, ' '));
        const count = typeof value === 'number' ? value : 1;
        return `${label} (${count})`;
      });
  }
  return [];
}

export default function CallNeedingAuditForm({
  view,
  logId,
  open,
  onClose
}: {
  view?: 'call-logs' | 'chat-logs' | 'system-sentiments';
  logId?: string;
  open?: boolean;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const { callNeedingAuditId: routeId } = useParams();
  const callLogId = logId ?? routeId;

  const resolvedView: InteractionLogFormProps['view'] =
    view === 'call-logs' ? 'agency-call-logs' : view === 'chat-logs' ? 'agency-chat-logs' : 'agency-system-sentiment';

  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [selectedCallLogId, setSelectedCallLogId] = useState<string | undefined>(callLogId);
  const [stickyContactId, setStickyContactId] = useState<number | undefined>();

  const interactionId = Number(selectedCallLogId);

  const { data: callLog, isLoading: isCallLogLoading } = useGetAgencyCallLog(selectedCallLogId, {
    include: 'contact,agent,business,intent_signal,email_logs,sms_logs'
  });
  const contactId = callLog?.contact_id ? Number(callLog.contact_id) : stickyContactId;

  useEffect(() => {
    if (callLog?.contact_id) {
      setStickyContactId(Number(callLog.contact_id));
    }
  }, [callLog?.contact_id]);

  const { data: associatedRecords, isLoading: isAssociatedRecordsLoading } = useGetAgencyAssociatedRecords(
    interactionId,
    contactId
  );
  const { data: contact, isLoading: isContactLoading } = useGetContact(interactionId, contactId!, {
    include: 'integration_entities'
  });
  const { data: notes } = useGetNotes(interactionId, contactId!);
  const { mutateAsync: createNote, isPending: isCreatePending } = useCreateNote(interactionId, contactId!);
  const { mutateAsync: updateNote, isPending: isUpdatePending } = useUpdateNote(interactionId, contactId!);
  const { mutateAsync: deleteNote } = useDeleteNote(interactionId, contactId!);

  const { handleSubmit, control, setValue, reset } = useForm<FormValues>({
    values: {
      contact: { phone_number: contact?.phone_number ?? null, call: callLog?.id || '' },
      note: { description: '' }
    }
  });

  useEffect(() => {
    if (!callLogId) return;
    setSelectedCallLogId(callLogId);
    setValue('contact.call', Number(callLogId));
  }, [callLogId, setValue]);

  const handleClose = () => {
    if (logId && onClose) onClose();
    else navigate('..');
  };

  const handleCreateOrUpdateNote = async (data: { note: { description: string } }) => {
    if (editingNote) {
      updateNote({ id: editingNote, payload: { note: data.note } });
      setEditingNote(null);
      reset();
    } else {
      await createNote({ note: data.note });
      reset();
    }
  };

  const handleEditClick = (note: { id: number; description: string }) => {
    setEditingNote(note.id);
    setValue('note.description', note.description);
  };

  const handleSelectCallLog = (id: number | undefined) => {
    setSelectedCallLogId(id != null ? String(id) : undefined);
  };

  const downloadTranscript = () => {
    const text = getVisibleJourneyItems(callLog?.interaction_journey)
      .map((item) => `${item.role === 'assistant' ? 'Agent' : 'Human'}: ${item?.content}`)
      .join('\n');
    const blob = new Blob([String(text)], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcript.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const callLogData = callLog as Record<string, unknown> | undefined;
  const signalData = (callLogData?.intent_signal ?? {}) as Record<string, unknown>;

  const formatLogDate = (date: string) => formatDate(date);
  const formatNoteDate = (date: string) => formatDate(date);
  const formatTranscriptTimestamp = (timestamp?: number) => formatUnixByTimezone(timestamp, undefined, 'h:mm:ss A');

  return (
    <InteractionLogForm
      view={resolvedView}
      isAgencyView
      isOpen={logId ? !!open : !!routeId}
      onClose={handleClose}
      isLoading={Boolean(selectedCallLogId) && (isCallLogLoading || isContactLoading || isAssociatedRecordsLoading)}
      callLog={callLog as InteractionLogFormProps['callLog']}
      callLogId={selectedCallLogId}
      contactId={contactId}
      contact={
        contact
          ? {
              phone_number: contact.phone_number,
              category: contact.category,
              chats_count: contact.chats_count,
              calls_count: contact.calls_count
            }
          : undefined
      }
      associatedRecords={associatedRecords}
      notes={notes}
      control={control}
      handleSubmit={handleSubmit}
      setValue={setValue}
      editingNote={editingNote}
      handleSelectCallLog={handleSelectCallLog}
      handleCreateOrUpdateNote={handleCreateOrUpdateNote}
      handleEditClick={handleEditClick}
      deleteNote={deleteNote}
      downloadTranscript={downloadTranscript}
      isCreatePending={isCreatePending}
      isUpdatePending={isUpdatePending}
      signalData={callLogData?.intent_signal ?? {}}
      endReason={
        (callLogData as { disconnection_reason?: string | null } | undefined)?.disconnection_reason ?? undefined
      }
      endReasonLabel={
        (callLogData as { disconnection_reason_label?: string | null } | undefined)?.disconnection_reason_label ??
        undefined
      }
      keywords={parseKeywords(signalData)}
      workflows={parseWorkflows(signalData)}
      taskCompleted={parseAgencyTaskCompleted(signalData)}
      systemSentiments={signalData.audit_issues as InteractionLogFormProps['systemSentiments']}
      businessName={(callLogData?.business_name as string) ?? (callLogData?.business as { name?: string })?.name ?? '—'}
      agentName={
        (callLogData?.agent_name as string) ??
        ((callLogData?.agent as { name?: string })?.name
          ? titleCase((callLogData?.agent as { name: string }).name)
          : '—')
      }
      formatLogDate={formatLogDate}
      formatNoteDate={formatNoteDate}
      formatTranscriptTimestamp={formatTranscriptTimestamp}
    />
  );
}
